import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { EnvelopeSimple, CheckCircle, Warning, Upload, Trash, PaperPlaneTilt, MagnifyingGlass, X, Plus, DownloadSimple } from "@phosphor-icons/react";

export default function OutreachPage() {
  const [tab, setTab] = useState("prospects");
  const [gmail, setGmail] = useState(null);
  const [gmailBusy, setGmailBusy] = useState(false);
  const [smtp, setSmtp] = useState(null);
  const [showSmtp, setShowSmtp] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const loadGmail = async () => {
    try { const { data } = await api.get("/gmail/status"); setGmail(data); }
    catch (e) { setGmail({ connected: false }); }
    try { const { data } = await api.get("/mail/status"); setSmtp(data); }
    catch { setSmtp({ connected: false }); }
  };
  useEffect(() => { loadGmail(); }, []);
  useEffect(() => {
    const email = searchParams.get("gmail_connected");
    if (email) { toast.success(`Gmail connected: ${email}`); loadGmail(); setSearchParams({}); }
  }, [searchParams]); // eslint-disable-line

  const connectGmail = async () => {
    setGmailBusy(true);
    try {
      const { data } = await api.get("/oauth/gmail/login");
      window.location.href = data.url;
    } catch (e) { toast.error("Failed to start Google sign-in"); setGmailBusy(false); }
  };
  const disconnectGmail = async () => {
    if (!window.confirm("Disconnect this Gmail? You'll have to reconnect to send more emails.")) return;
    try { await api.post("/gmail/disconnect", {}); toast.success("Gmail disconnected"); loadGmail(); }
    catch { toast.error("Disconnect failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]" data-testid="outreach-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-micro">Growth · Cold outreach</div>
          <h1 className="font-display text-2xl sm:text-3xl font-black mt-1">Outreach</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">Upload a prospect list, connect your Gmail, and send AI-personalized outreach — all from your own inbox so replies land like normal.</p>
        </div>
        <CronHealthBadge/>
      </div>

      {/* Gmail connect band */}
      <section className="mt-6 bg-white border border-slate-200 rounded-md p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="outreach-gmail-band">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full grid place-items-center ${gmail?.connected ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
            {gmail?.connected ? <CheckCircle size={20} weight="fill"/> : <EnvelopeSimple size={20}/>}
          </div>
          <div>
            <div className="font-semibold text-sm text-slate-900">{gmail?.connected ? `Gmail: ${gmail.email}` : "Connect your Gmail to send outreach"}</div>
            <div className="text-xs text-slate-500">{gmail?.connected ? `Connected ${new Date(gmail.connected_at).toLocaleDateString()}` : "One-click Google OAuth"}</div>
          </div>
        </div>
        {gmail?.connected ? (
          <div className="flex gap-2"><TestSendButton/><button onClick={disconnectGmail} data-testid="gmail-disconnect" className="text-xs border border-red-300 text-red-600 rounded-sm px-3 py-1.5 hover:bg-red-600 hover:text-white">Disconnect</button></div>
        ) : (
          <button onClick={connectGmail} disabled={gmailBusy} data-testid="gmail-connect"
                  className="text-sm inline-flex items-center gap-2 bg-slate-900 hover:bg-blue-700 text-white rounded-sm px-4 py-2 font-semibold disabled:opacity-50">
            <EnvelopeSimple size={14}/>{gmailBusy ? "Redirecting…" : "Connect Gmail"}
          </button>
        )}
      </section>

      {/* Generic SMTP/IMAP band */}
      <section className="mt-3 bg-white border border-slate-200 rounded-md p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="outreach-smtp-band">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full grid place-items-center ${smtp?.connected ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
            {smtp?.connected ? <CheckCircle size={20} weight="fill"/> : <EnvelopeSimple size={20}/>}
          </div>
          <div>
            <div className="font-semibold text-sm text-slate-900">{smtp?.connected ? `SMTP: ${smtp.email}` : "Or connect Zoho / Outlook / any inbox (SMTP + IMAP)"}</div>
            <div className="text-xs text-slate-500">{smtp?.connected ? `${smtp.smtp_host || ""} · replies polled every 5 min` : "Universal connector — works with any mailbox that supports SMTP + IMAP"}</div>
          </div>
        </div>
        {smtp?.connected ? (
          <button onClick={async () => { if (!window.confirm("Disconnect this SMTP inbox?")) return; try { await api.post("/mail/disconnect", {}); toast.success("Disconnected"); loadGmail(); } catch { toast.error("Failed"); } }}
                  data-testid="smtp-disconnect" className="text-xs border border-red-300 text-red-600 rounded-sm px-3 py-1.5 hover:bg-red-600 hover:text-white">Disconnect</button>
        ) : (
          <button onClick={() => setShowSmtp(true)} data-testid="smtp-connect"
                  className="text-sm inline-flex items-center gap-2 border border-slate-300 rounded-sm px-4 py-2 hover:bg-slate-900 hover:text-white font-semibold">
            <EnvelopeSimple size={14}/>Connect email account
          </button>
        )}
      </section>
      {showSmtp && <SmtpConnectModal onCancel={() => setShowSmtp(false)} onSaved={() => { setShowSmtp(false); loadGmail(); toast.success("Email account connected"); }}/>}

      {/* Tabs */}
      <div className="mt-6 border-b border-slate-200 flex items-center gap-1">
        {[{ k: "prospects", label: "Prospects" }, { k: "campaigns", label: "Campaigns" }, { k: "analytics", label: "Analytics" }, { k: "settings", label: "Settings" }].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
                  data-testid={`outreach-tab-${t.k}`}
                  className={`text-xs font-semibold px-3 py-2 border-b-2 transition-colors ${tab === t.k ? "border-blue-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "prospects" && <ProspectsTab/>}
      {tab === "campaigns" && <CampaignsTab mailConnected={gmail?.connected || smtp?.connected}/>}
      {tab === "analytics" && <AnalyticsTab/>}
      {tab === "settings" && <OutreachSettingsTab/>}
    </div>
  );
}

function OutreachSettingsTab() {
  const [cfg, setCfg] = useState({ autocreate_enabled: false, send_stage_col_id: "", reply_stage_col_id: "" });
  const [cols, setCols] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { (async () => {
    try { const { data: s } = await api.get("/outreach/settings"); setCfg({ autocreate_enabled: !!s.autocreate_enabled, send_stage_col_id: s.send_stage_col_id || "", reply_stage_col_id: s.reply_stage_col_id || "" }); } catch {}
    try { const { data: c } = await api.get("/crm/kanban/columns"); setCols(c.columns || []); } catch {}
  })(); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.put("/outreach/settings", cfg); toast.success("Settings saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-6 max-w-2xl space-y-4" data-testid="outreach-settings">
      <section className="bg-white border border-slate-200 rounded-md p-5">
        <h3 className="font-display font-bold text-lg">CRM auto-create</h3>
        <p className="text-xs text-slate-500 mt-1 mb-4">When outreach goes out, we can spin up a CRM deal for that prospect and route it through your Kanban as replies come in.</p>
        <label className="flex items-center gap-2 mb-4">
          <input type="checkbox" checked={cfg.autocreate_enabled} onChange={(e) => setCfg({ ...cfg, autocreate_enabled: e.target.checked })} data-testid="outreach-autocreate-toggle"/>
          <span className="text-sm font-semibold">Create a CRM deal every time the first step of a campaign is sent</span>
        </label>
        <label className="block mb-3"><span className="label-micro">Stage when email is sent</span>
          <select value={cfg.send_stage_col_id} onChange={(e) => setCfg({ ...cfg, send_stage_col_id: e.target.value })} data-testid="outreach-send-stage"
                  className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm">
            <option value="">— Pick a Kanban stage —</option>
            {cols.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></label>
        <label className="block"><span className="label-micro">Stage when prospect replies</span>
          <select value={cfg.reply_stage_col_id} onChange={(e) => setCfg({ ...cfg, reply_stage_col_id: e.target.value })} data-testid="outreach-reply-stage"
                  className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm">
            <option value="">— Pick a Kanban stage —</option>
            {cols.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></label>
        <div className="flex justify-end mt-4">
          <button onClick={save} disabled={busy} data-testid="outreach-settings-save"
                  className="text-sm bg-slate-900 hover:bg-blue-700 text-white rounded-sm px-4 py-2 font-semibold disabled:opacity-50">{busy ? "Saving…" : "Save settings"}</button>
        </div>
      </section>
    </div>
  );
}

function CampaignsTab({ mailConnected }) {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const load = async () => { try { const { data } = await api.get("/campaigns"); setRows(data.campaigns || []); } catch {} };
  useEffect(() => { load(); }, []);
  const launch = async (id) => { try { const { data } = await api.post(`/campaigns/${id}/launch`); const via = data.mailbox_email ? ` via ${data.mailbox_email}` : ""; toast.success(`Enrolled ${data.enrolled} prospects${via}`, data.reassigned && data.mailbox_email ? { description: `Sending through the shared team inbox (${data.mailbox_email}). Your personal inbox isn't used.` } : undefined); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Launch failed"); } };
  const tickNow = async (id) => {
    // Snapshot before tick so we can show "N of M sent" progress.
    const before = rows.find(r => r.id === id) || {};
    const beforeSent = before.sent || 0;
    const target = before.pending || 0;
    const tid = toast.loading(target ? `Sending 0 of ${target}…` : "Firing tick…");
    try {
      const { data } = await api.post(`/campaigns/${id}/tick-now`);
      // Poll the campaigns list to stream the actual sent-count delta
      let poll = 0, lastSent = beforeSent;
      const timer = setInterval(async () => {
        poll += 1;
        try {
          const { data: list } = await api.get("/campaigns");
          setRows(list.campaigns || []);
          const cur = (list.campaigns || []).find(r => r.id === id) || {};
          const sent = (cur.sent || 0) - beforeSent;
          if (target > 0 && sent >= target) {
            clearInterval(timer);
            toast.success(`Delivered ${sent} of ${target}`, { id: tid, description: `${cur.replied || 0} replies · ${data.bounced || 0} bounced so far. Next tick in ~5 min.` });
          } else if (poll >= 8) {
            clearInterval(timer);
            toast.success(`Sent ${sent} of ${target}`, { id: tid, description: `Remaining ${Math.max(0, target - sent)} will fire on the next cron (~5 min).` });
          } else if (sent !== lastSent) {
            lastSent = sent;
            toast.loading(`Sending ${sent} of ${target}…`, { id: tid });
          }
        } catch { /* keep polling */ }
      }, 1500);
      // Kickoff refresh immediately so the queue column updates
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send-now failed", { id: tid });
    }
  };
  const del = async (id) => { if (!window.confirm("Delete campaign + events?")) return; try { await api.delete(`/campaigns/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); } };
  const retryFailed = async (id) => {
    try {
      const { data } = await api.post(`/campaigns/${id}/retry-failed`);
      toast.success(data.requeued ? `Re-queued ${data.requeued} failed send${data.requeued === 1 ? "" : "s"} — will retry on the next tick.` : "Nothing to retry.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Retry failed"); }
  };
  const fmtNext = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso), diff = (d.getTime() - Date.now()) / 1000;
    if (diff <= 0) return "due now · fires on next tick (≤5 min)";
    if (diff < 60) return `in ${Math.floor(diff)}s`;
    if (diff < 3600) return `in ${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `in ${Math.floor(diff / 3600)}h`;
    return d.toLocaleString();
  };
  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-slate-600">{rows.length} campaigns</div>
        <button onClick={() => setCreating(true)} disabled={!mailConnected} data-testid="new-campaign-btn"
                className="text-sm inline-flex items-center gap-1.5 bg-slate-900 hover:bg-blue-700 disabled:opacity-40 text-white rounded-sm px-4 py-2 font-semibold">
          <Plus size={13}/>New campaign
        </button>
      </div>
      {!mailConnected && <div className="mb-4 text-xs bg-amber-50 border border-amber-200 rounded-sm p-3 text-amber-800 flex items-center gap-2"><Warning size={13} weight="fill"/>Connect Gmail or an SMTP/IMAP mailbox above before you can create a campaign.</div>}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="campaigns-empty">No campaigns yet. Click <b>New campaign</b> to build your first sequence.</div>
        ) : (
          <table className="w-full text-sm" data-testid="campaigns-table">
            <thead><tr className="text-left border-b border-slate-200">
              <th className="label-micro py-2 px-3">Name</th><th className="label-micro py-2 px-3">Status</th>
              <th className="label-micro py-2 px-3">Enrolled</th><th className="label-micro py-2 px-3">Sent</th>
              <th className="label-micro py-2 px-3">Opened</th><th className="label-micro py-2 px-3">Clicked</th>
              <th className="label-micro py-2 px-3">Replied</th>
              <th className="label-micro py-2 px-3">Queue · next</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map(c => (
                <tr key={c.id} className="border-b border-slate-100 group" data-testid={`campaign-row-${c.id}`}>
                  <td className="py-2.5 px-3 font-semibold">{c.name}</td>
                  <td className="py-2.5 px-3"><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${c.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{c.status}</span></td>
                  <td className="py-2.5 px-3 tabular-nums">{c.enrolled}</td>
                  <td className="py-2.5 px-3 tabular-nums">{c.sent}</td>
                  <td className="py-2.5 px-3 tabular-nums">{c.opened}</td>
                  <td className="py-2.5 px-3 tabular-nums">{c.clicked}</td>
                  <td className="py-2.5 px-3 tabular-nums text-emerald-700 font-bold">{c.replied}</td>
                  <td className="py-2.5 px-3 text-[10px] text-slate-500" data-testid={`campaign-queue-${c.id}`}>
                    {c.status === "active" ? <><b className="tabular-nums text-slate-800">{c.pending || 0}</b> pending · <span title={c.next_send_at || ""}>{fmtNext(c.next_send_at)}</span></> : "—"}
                  </td>
                  <td className="py-2.5 px-3 text-right whitespace-nowrap">
                    {c.status === "draft" && <button onClick={() => launch(c.id)} data-testid={`campaign-launch-${c.id}`} className="text-[10px] font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm px-2 py-1 mr-2">Launch</button>}
                    {c.status === "active" && (c.pending || 0) > 0 && <button onClick={() => tickNow(c.id)} data-testid={`campaign-tick-now-${c.id}`} title="Fire the outreach tick immediately instead of waiting for the 5-min cron" className="text-[10px] font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-2 py-1 mr-2">Send now</button>}
                    {(c.failed || 0) > 0 && <button onClick={() => retryFailed(c.id)} data-testid={`campaign-retry-failed-${c.id}`} title="These sends failed (e.g. mailbox was disconnected at send time) — reconnect the mailbox first, then retry" className="text-[10px] font-semibold bg-amber-500 hover:bg-amber-600 text-white rounded-sm px-2 py-1 mr-2">Retry {c.failed} failed</button>}
                    <button onClick={() => del(c.id)} className="text-[10px] text-red-600 opacity-0 group-hover:opacity-100"><Trash size={12}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {creating && <CampaignWizard onCancel={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }}/>}
    </div>
  );
}

// --- Wizard Cancel now also GCs an orphan draft if Launch failed mid-flight (iter25).

function CampaignWizard({ onCancel, onSaved }) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [fromName, setFromName] = useState("");
  const [filter, setFilter] = useState({ company: "", title: "", status: "" });
  const [steps, setSteps] = useState([{ subject: "", html_body: "", ai_prompt: "", delay_days_after: 3 }]);
  const [dailyCap, setDailyCap] = useState(100);
  const [busy, setBusy] = useState(false);
  const [previewIdx, setPreviewIdx] = useState(null);
  const [previewOut, setPreviewOut] = useState("");
  // Track the draft id once /campaigns POST succeeds so a Launch failure doesn't
  // orphan a duplicate draft on retry (fix from iter24 review).
  const createdIdRef = useRef(null);

  const updStep = (i, k, v) => setSteps(s => s.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const addStep = () => setSteps(s => [...s, { subject: "", html_body: "", ai_prompt: "", delay_days_after: 3 }]);
  const removeStep = (i) => setSteps(s => s.filter((_, j) => j !== i));

  const preview = async (i) => {
    setPreviewIdx(i); setPreviewOut("Drafting…");
    try {
      const { data } = await api.post("/campaigns/personalize", { template: steps[i].html_body, ai_prompt: steps[i].ai_prompt, prospect: { first_name: "Zoya", last_name: "Kapoor", company: "Zephyr Retail", title: "Founder", email: "zoya@zephyr.ai" } });
      setPreviewOut(data.preview);
    } catch (e) { setPreviewOut("Preview failed."); }
  };

  const save = async (launchNow = false) => {
    if (!name.trim()) return toast.error("Give the campaign a name");
    if (!steps.length || !steps[0].subject || !steps[0].html_body) return toast.error("First step needs subject + body");
    setBusy(true);
    try {
      // 1. Create draft ONCE — reuse the id on retry
      let campaignId = createdIdRef.current;
      if (!campaignId) {
        const { data } = await api.post("/campaigns", { name, from_name: fromName, audience_filter: filter, steps, schedule: { daily_cap: dailyCap } });
        campaignId = data.id;
        createdIdRef.current = campaignId;
      } else {
        // Push latest edits onto the existing draft; surface errors so the
        // user isn't launching stale content silently.
        try { await api.patch(`/campaigns/${campaignId}`, { name, from_name: fromName, audience_filter: filter, steps, schedule: { daily_cap: dailyCap } }); }
        catch (e) { toast.error(e?.response?.data?.detail || "Could not update draft — launching original content."); }
      }
      // 2. Optionally launch
      if (launchNow) {
        const r = await api.post(`/campaigns/${campaignId}/launch`);
        const via = r.data.mailbox_email ? ` via ${r.data.mailbox_email}` : "";
        toast.success(`Launched — ${r.data.enrolled} enrolled${via}`,
          r.data.reassigned && r.data.mailbox_email
            ? { description: `Sending through the shared team inbox (${r.data.mailbox_email}). Your personal inbox isn't used.` }
            : undefined);
      } else {
        toast.success("Draft saved");
      }
      createdIdRef.current = null;
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const cancelWizard = async () => {
    // If the user created a draft but never launched, GC it on Cancel so the
    // Campaigns list doesn't fill up with abandoned drafts.
    const orphanId = createdIdRef.current;
    if (orphanId) {
      try { await api.delete(`/campaigns/${orphanId}`); }
      catch (e) { toast.error(`Couldn't clean up draft — remove "${name || orphanId.slice(0,8)}" manually.`); }
      createdIdRef.current = null;
    }
    onCancel();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="campaign-wizard">
      <div className="absolute inset-0 bg-slate-900/50" onClick={cancelWizard}/>
      <div className="relative bg-white rounded-sm shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
          <h2 className="font-display font-bold text-lg">New campaign · Step {step} of 5</h2>
          <button onClick={cancelWizard} data-testid="wiz-cancel" className="p-1.5 rounded-sm hover:bg-slate-100"><X size={16}/></button>
        </div>
        <div className="p-5 space-y-4">
          {step === 1 && (<>
            <label className="block"><span className="label-micro">Campaign name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="wiz-name" className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"/></label>
            <label className="block"><span className="label-micro">Send emails as (from name, optional)</span>
              <input value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Raghav from TNAI" data-testid="wiz-from-name" className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"/></label>
          </>)}
          {step === 2 && (<div className="space-y-3">
            <div className="text-sm font-semibold">Audience filter — leave blank to include everyone</div>
            <input value={filter.company} onChange={(e) => setFilter({ ...filter, company: e.target.value })} placeholder="Company contains…" data-testid="wiz-filter-company" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm"/>
            <input value={filter.title} onChange={(e) => setFilter({ ...filter, title: e.target.value })} placeholder="Title contains… (e.g. Founder, CEO)" data-testid="wiz-filter-title" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm"/>
            <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} data-testid="wiz-filter-status" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm">
              <option value="">Any status</option><option value="new">New</option><option value="contacted">Contacted</option><option value="replied">Replied</option>
            </select>
          </div>)}
          {step === 3 && (<div className="space-y-3">
            {steps.map((s, i) => (
              <div key={i} className="border border-slate-200 rounded-sm p-3 space-y-2" data-testid={`wiz-step-${i}`}>
                <div className="flex items-center justify-between"><b className="text-sm">Step {i + 1}{i === 0 ? " (initial)" : ` · sends ${steps[i-1].delay_days_after}d after previous`}</b>
                  {i > 0 && <button onClick={() => removeStep(i)} className="text-[10px] text-red-600"><Trash size={11}/></button>}</div>
                <input value={s.subject} onChange={(e) => updStep(i, "subject", e.target.value)} placeholder="Subject — use {{first_name}} {{company}}" data-testid={`wiz-step-${i}-subject`} className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm"/>
                <textarea rows={4} value={s.html_body} onChange={(e) => updStep(i, "html_body", e.target.value)} placeholder="Body HTML — {{first_name}}, {{company}}, {{title}} merge in" data-testid={`wiz-step-${i}-body`} className="w-full border border-slate-300 rounded-sm px-3 py-2 text-xs font-mono"/>
                <textarea rows={2} value={s.ai_prompt} onChange={(e) => updStep(i, "ai_prompt", e.target.value)} placeholder="AI prompt (optional) — e.g. 'Write a 90-word intro referencing their retail industry'" data-testid={`wiz-step-${i}-ai`} className="w-full border border-slate-300 rounded-sm px-3 py-2 text-xs"/>
                <div className="flex items-center gap-2"><span className="text-[10px] text-slate-500">Next step delay:</span>
                  <input type="number" min={0} value={s.delay_days_after} onChange={(e) => updStep(i, "delay_days_after", Number(e.target.value))} className="w-16 border border-slate-300 rounded-sm px-2 py-1 text-xs"/><span className="text-[10px] text-slate-500">days</span>
                  <button onClick={() => preview(i)} data-testid={`wiz-step-${i}-preview`} className="text-[10px] ml-auto border border-slate-300 rounded-sm px-2 py-1 hover:bg-slate-900 hover:text-white">Preview with AI</button>
                </div>
                {previewIdx === i && <div className="border-l-2 border-blue-400 pl-3 text-xs text-slate-700 whitespace-pre-wrap max-h-48 overflow-y-auto" data-testid="wiz-preview-out">{previewOut}</div>}
              </div>
            ))}
            <button onClick={addStep} data-testid="wiz-add-step" className="text-xs w-full border border-dashed border-slate-300 rounded-sm py-2 hover:bg-slate-50">+ Add follow-up step</button>
          </div>)}
          {step === 4 && (<label className="block"><span className="label-micro">Daily send cap (Gmail free: 500 / day, Workspace: 2000)</span>
            <input type="number" min={1} max={2000} value={dailyCap} onChange={(e) => setDailyCap(Number(e.target.value))} data-testid="wiz-daily-cap" className="mt-1 w-40 border border-slate-300 rounded-sm px-3 py-2 text-sm"/>
            <p className="text-xs text-slate-500 mt-2">The engine ticks every 5 minutes and sends up to this many emails per calendar day.</p></label>)}
          {step === 5 && (<div className="text-sm">
            <div className="font-semibold mb-2">Ready to launch?</div>
            <ul className="text-xs text-slate-700 space-y-1">
              <li>• Campaign: <b>{name}</b></li>
              <li>• Steps: <b>{steps.length}</b></li>
              <li>• Filter: company ~ "{filter.company || 'any'}", title ~ "{filter.title || 'any'}", status = {filter.status || 'any'}</li>
              <li>• Daily cap: <b>{dailyCap}</b></li>
            </ul>
            <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm p-2">Launching enrolls matching prospects immediately. The first step goes out on the next tick (≤ 5 min).</div>
          </div>)}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-between">
          <button onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1} className="text-sm border border-slate-300 rounded-sm px-4 py-2 hover:bg-slate-100 disabled:opacity-40">Back</button>
          <div className="flex gap-2">
            {step < 5 && <button onClick={() => setStep(step + 1)} data-testid="wiz-next" className="text-sm bg-slate-900 hover:bg-blue-700 text-white rounded-sm px-4 py-2">Next</button>}
            {step === 5 && <><button onClick={() => save(false)} disabled={busy} data-testid="wiz-save-draft" className="text-sm border border-slate-300 rounded-sm px-4 py-2 hover:bg-slate-100">Save draft</button>
              <button onClick={() => save(true)} disabled={busy} data-testid="wiz-launch" className="text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm px-4 py-2">{busy ? "Launching…" : "Launch now"}</button></>}
          </div>
        </div>
      </div>
    </div>
  );
}

function AnalyticsTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { (async () => { try { const { data } = await api.get("/campaigns"); setRows(data.campaigns || []); } catch {} })(); }, []);
  const totals = rows.reduce((a, c) => ({ sent: a.sent + c.sent, opened: a.opened + c.opened, clicked: a.clicked + c.clicked, replied: a.replied + c.replied }), { sent: 0, opened: 0, clicked: 0, replied: 0 });
  const pct = (n, d) => d > 0 ? `${((n / d) * 100).toFixed(1)}%` : "—";
  return (
    <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="outreach-analytics">
      <Tile label="Total sent" value={totals.sent} sub=""/>
      <Tile label="Open rate" value={pct(totals.opened, totals.sent)} sub={`${totals.opened} opens`}/>
      <Tile label="Click rate" value={pct(totals.clicked, totals.sent)} sub={`${totals.clicked} clicks`}/>
      <Tile label="Reply rate" value={pct(totals.replied, totals.sent)} sub={`${totals.replied} replies`}/>
    </div>
  );
}
function Tile({ label, value, sub }) { return <div className="bg-white border border-slate-200 rounded-md p-4"><div className="label-micro">{label}</div><div className="font-display font-black text-2xl mt-1">{value}</div>{sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}</div>; }

function SmtpConnectModal({ onCancel, onSaved }) {
  const [preset, setPreset] = useState("zoho_in");
  const PRESETS = {
    zoho_in:  { smtp_host: "smtp.zoho.in",       smtp_port: 465, imap_host: "imap.zoho.in",           imap_port: 993, label: "Zoho (India)" },
    zoho_com: { smtp_host: "smtp.zoho.com",      smtp_port: 465, imap_host: "imap.zoho.com",          imap_port: 993, label: "Zoho (Global)" },
    outlook:  { smtp_host: "smtp.office365.com", smtp_port: 587, imap_host: "outlook.office365.com",  imap_port: 993, label: "Outlook / Microsoft 365" },
    gmail:    { smtp_host: "smtp.gmail.com",     smtp_port: 465, imap_host: "imap.gmail.com",         imap_port: 993, label: "Gmail (via app password)" },
    custom:   { smtp_host: "",                   smtp_port: 465, imap_host: "",                       imap_port: 993, label: "Custom" },
  };
  const [cfg, setCfg] = useState({ email: "", password: "", from_name: "", ...PRESETS.zoho_in });
  const [busy, setBusy] = useState(false);
  const pick = (k) => { setPreset(k); const { label, ...rest } = PRESETS[k]; setCfg({ ...cfg, ...rest }); };
  const save = async () => {
    if (!cfg.email || !cfg.password) return toast.error("Email + app password required");
    setBusy(true);
    const { label, ...payload } = cfg;
    try { await api.post("/mail/connect", payload); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Connect failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="smtp-modal">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onCancel}/>
      <div className="relative bg-white rounded-sm shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-5">
        <div className="flex items-center justify-between mb-3"><h3 className="font-display font-bold text-lg">Connect email account</h3><button onClick={onCancel} className="p-1 rounded-sm hover:bg-slate-100"><X size={16}/></button></div>
        <div className="text-xs text-slate-500 mb-3">Use an <b>app-specific password</b> — not your login password. Zoho: My Account → Security → App passwords.</div>
        <label className="block mb-2"><span className="label-micro">Provider</span>
          <select value={preset} onChange={(e) => pick(e.target.value)} data-testid="smtp-preset"
                  className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm">
            {Object.entries(PRESETS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select></label>
        <label className="block mb-2"><span className="label-micro">Email address</span>
          <input value={cfg.email} onChange={(e) => setCfg({ ...cfg, email: e.target.value })} data-testid="smtp-email" placeholder="you@yourdomain.com"
                 className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm"/></label>
        <label className="block mb-2"><span className="label-micro">App-specific password</span>
          <input type="password" value={cfg.password} onChange={(e) => setCfg({ ...cfg, password: e.target.value })} data-testid="smtp-password"
                 className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm"/></label>
        <label className="block mb-3"><span className="label-micro">From name (optional)</span>
          <input value={cfg.from_name} onChange={(e) => setCfg({ ...cfg, from_name: e.target.value })} data-testid="smtp-from-name" placeholder="Raghav from TNAI"
                 className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm"/></label>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <label><span className="label-micro">SMTP host</span><input value={cfg.smtp_host} onChange={(e) => setCfg({ ...cfg, smtp_host: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-xs"/></label>
          <label><span className="label-micro">SMTP port</span><input type="number" value={cfg.smtp_port} onChange={(e) => setCfg({ ...cfg, smtp_port: Number(e.target.value) })} className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-xs"/></label>
          <label><span className="label-micro">IMAP host</span><input value={cfg.imap_host} onChange={(e) => setCfg({ ...cfg, imap_host: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-xs"/></label>
          <label><span className="label-micro">IMAP port</span><input type="number" value={cfg.imap_port} onChange={(e) => setCfg({ ...cfg, imap_port: Number(e.target.value) })} className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-xs"/></label>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <button onClick={onCancel} className="text-sm border border-slate-300 rounded-sm px-4 py-2 hover:bg-slate-100">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="smtp-save"
                  className="text-sm bg-slate-900 hover:bg-blue-700 text-white rounded-sm px-4 py-2 disabled:opacity-50">{busy ? "Verifying…" : "Test & Connect"}</button>
        </div>
      </div>
    </div>
  );
}

function CronHealthBadge() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const { data } = await api.get("/outreach/cron-status"); setS(data); }
    catch { setS({ last_tick_at: null }); }
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  const runNow = async () => {
    setBusy(true);
    const tid = toast.loading("Firing outreach tick…");
    try {
      const { data } = await api.post("/outreach/tick-now");
      toast.success(`Tick done · sent ${data.sent || 0} · replied ${data.replied || 0} · bounced ${data.bounced || 0}`, { id: tid });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tick failed", { id: tid });
    } finally { setBusy(false); }
  };
  if (!s) return null;
  const at = s.last_tick_at ? new Date(s.last_tick_at) : null;
  const ageMin = at ? (Date.now() - at.getTime()) / 60000 : Infinity;
  const failed = s.last_failed || 0;
  // A recent heartbeat only means the tick endpoint ran — it says nothing
  // about whether any send actually succeeded. If the last tick recorded
  // failures, don't show "Healthy"; that hides a broken mailbox connection.
  const tone = failed > 0 ? "amber" : ageMin <= 10 ? "emerald" : ageMin <= 20 ? "amber" : "rose";
  const tones = { emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
                  amber: "bg-amber-100 text-amber-700 border-amber-200",
                  rose:  "bg-rose-100 text-rose-700 border-rose-200" };
  const label = !at ? "Cron never ran" : ageMin < 1 ? "just now" :
                ageMin < 60 ? `${Math.floor(ageMin)}m ago` :
                ageMin < 1440 ? `${Math.floor(ageMin / 60)}h ago` :
                at.toLocaleDateString();
  const status = failed > 0 ? "Ran, but sends failed" : tone === "emerald" ? "Healthy" : tone === "amber" ? "Slow" : "Stalled";
  return (
    <div className="flex items-center gap-2" data-testid="cron-health-wrapper">
      <div className={`inline-flex items-center gap-2 border rounded-sm px-3 py-1.5 text-[11px] ${tones[tone]}`}
           data-testid="cron-health-badge" title={at ? `Last tick: ${at.toLocaleString()} · scanned ${s.last_scanned} campaigns · sent ${s.last_sent} · replied ${s.last_replied} · bounced ${s.last_bounced} · failed ${failed}` : "The outreach cron has never checked in. Click Run now, or ask an admin to verify the scheduler on the deployed pod."}>
        <span className={`w-2 h-2 rounded-full ${tone === "emerald" ? "bg-emerald-500" : tone === "amber" ? "bg-amber-500" : "bg-rose-500"} animate-pulse`}/>
        <span className="font-bold">Cron · {status}</span>
        <span className="opacity-80">Last tick {label}</span>
        {failed > 0 && <span className="font-bold" data-testid="cron-failed-count">· {failed} send{failed === 1 ? "" : "s"} failed</span>}
        {tone !== "emerald" && <span className="opacity-70">· runs every {s.cron_interval_minutes || 5} min</span>}
      </div>
      {tone !== "emerald" && (
        <button onClick={runNow} disabled={busy} data-testid="cron-run-now"
                title="Run the outreach tick right now (admin)"
                className="text-[11px] font-bold bg-slate-900 hover:bg-blue-700 text-white rounded-sm px-3 py-1.5 disabled:opacity-50">
          {busy ? "Running…" : "Run now"}
        </button>
      )}
    </div>
  );
}

function TestSendButton() {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!to.trim()) return;
    setBusy(true);
    try { await api.post("/gmail/test", { to }); toast.success("Test sent — check your inbox"); setOpen(false); setTo(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="gmail-test-btn"
              className="text-xs border border-slate-300 rounded-sm px-3 py-1.5 hover:bg-slate-900 hover:text-white inline-flex items-center gap-1"><PaperPlaneTilt size={11}/>Send test</button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setOpen(false)}/>
          <div className="relative bg-white rounded-sm shadow-2xl max-w-md w-full p-5">
            <div className="flex items-center justify-between mb-3"><h3 className="font-display font-bold">Send a test email</h3><button onClick={() => setOpen(false)} className="p-1 rounded-sm hover:bg-slate-100"><X size={14}/></button></div>
            <label className="block"><span className="label-micro">To</span>
              <input value={to} onChange={(e) => setTo(e.target.value)} autoFocus placeholder="you@example.com" data-testid="gmail-test-to"
                     className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/></label>
            <div className="flex justify-end mt-4">
              <button onClick={send} disabled={busy} data-testid="gmail-test-send" className="text-sm bg-slate-900 hover:bg-blue-700 text-white rounded-sm px-4 py-2 disabled:opacity-50">{busy ? "Sending…" : "Send"}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ProspectsTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = async () => {
    setBusy(true);
    try { const { data } = await api.get("/prospects", { params: { q, limit: 200 } });
      setRows(data.prospects || []); setTotal(data.total || 0);
    } catch (e) { toast.error("Failed to load prospects"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]);

  const del = async (id) => {
    if (!window.confirm("Delete this prospect?")) return;
    try { await api.delete(`/prospects/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="mt-6">
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search prospects…"
                 data-testid="prospects-search"
                 className="w-full border border-slate-300 rounded-sm pl-9 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
        <div className="text-xs text-slate-500 mr-auto">Showing <b>{rows.length}</b> of <b>{total}</b></div>
        <button onClick={() => setImporting(true)} data-testid="prospects-import-btn"
                className="text-sm inline-flex items-center gap-1.5 bg-slate-900 hover:bg-blue-700 text-white rounded-sm px-4 py-2 font-semibold">
          <Upload size={13}/>Import CSV
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {busy && rows.length === 0 && <div className="p-10 text-center text-sm text-slate-500">Loading…</div>}
        {!busy && rows.length === 0 && (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="prospects-empty">
            No prospects yet. Click <b>Import CSV</b> to upload your first list.
          </div>
        )}
        {rows.length > 0 && (
          <table className="w-full text-sm" data-testid="prospects-table">
            <thead>
              <tr className="text-left border-b border-slate-200">
                <th className="label-micro py-2 px-3">Name</th>
                <th className="label-micro py-2 px-3">Email</th>
                <th className="label-micro py-2 px-3">Company</th>
                <th className="label-micro py-2 px-3">Title</th>
                <th className="label-micro py-2 px-3">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(p => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50 group" data-testid={`prospect-row-${p.id}`}>
                  <td className="py-2.5 px-3">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-slate-800 text-white grid place-items-center text-[9px] font-bold">{((p.first_name || p.email) || "?").slice(0,1).toUpperCase()}</span>
                      <span className="truncate">{[p.first_name, p.last_name].filter(Boolean).join(" ") || "—"}</span>
                    </div>
                  </td>
                  <td className="py-2.5 px-3 truncate">{p.email}{p.unsubscribed && <span className="ml-1 text-[9px] font-bold px-1 py-0.5 bg-red-100 text-red-700 rounded-sm">UNSUB</span>}</td>
                  <td className="py-2.5 px-3 truncate">{p.company || "—"}</td>
                  <td className="py-2.5 px-3 truncate">{p.title || "—"}</td>
                  <td className="py-2.5 px-3"><span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{p.status || "new"}</span></td>
                  <td className="py-2.5 px-3 text-right">
                    <button onClick={() => del(p.id)} data-testid={`prospect-del-${p.id}`}
                            className="text-[10px] text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"><Trash size={12}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {importing && <ImportProspectsModal onCancel={() => setImporting(false)} onDone={() => { setImporting(false); load(); }}/>}
    </div>
  );
}

function ImportProspectsModal({ onCancel, onDone }) {
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);
  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result || ""));
    reader.readAsText(f);
  };
  const downloadTemplate = async () => {
    try {
      const resp = await api.get("/prospects/csv-template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "prospects-template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Template download failed"); }
  };
  const run = async () => {
    if (!csv.trim()) { toast.error("Paste some CSV or upload a file"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/prospects/import", { csv });
      toast.success(`Imported: ${data.inserted} new · ${data.updated} updated · ${data.skipped} skipped`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="prospects-import-modal">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onCancel}/>
      <div className="relative bg-white rounded-sm shadow-2xl max-w-2xl w-full p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-lg">Import prospects</h3>
          <button onClick={onCancel} className="p-1 rounded-sm hover:bg-slate-100"><X size={16}/></button>
        </div>
        <div className="text-xs text-slate-600 mb-3">Paste a CSV or upload a file. Column headers we understand: <code className="bg-slate-100 px-1">email</code>, <code className="bg-slate-100 px-1">first_name</code>, <code className="bg-slate-100 px-1">last_name</code>, <code className="bg-slate-100 px-1">company</code>, <code className="bg-slate-100 px-1">title</code>, <code className="bg-slate-100 px-1">phone</code>, <code className="bg-slate-100 px-1">linkedin</code>, <code className="bg-slate-100 px-1">notes</code>. Anything else lands in a custom-fields bag. Prospects are keyed by email — re-uploading updates existing rows.</div>
        <button onClick={downloadTemplate} data-testid="prospects-download-template"
                className="text-xs inline-flex items-center gap-1.5 border border-slate-300 rounded-sm px-3 py-1.5 hover:bg-slate-900 hover:text-white mb-3">
          <DownloadSimple size={13}/>Download sample CSV template
        </button>
        <input type="file" accept=".csv,text/csv" onChange={onFile} data-testid="prospects-file-input" className="text-xs mb-2 ml-2"/>
        <textarea rows={8} value={csv} onChange={(e) => setCsv(e.target.value)} data-testid="prospects-csv-textarea"
                  placeholder="email,first_name,last_name,company,title\nfounder@zephyr.ai,Zoya,Kapoor,Zephyr Retail,Founder"
                  className="w-full border border-slate-300 rounded-sm px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"/>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onCancel} className="text-sm border border-slate-300 rounded-sm px-4 py-2 hover:bg-slate-100">Cancel</button>
          <button onClick={run} disabled={busy} data-testid="prospects-import-run"
                  className="text-sm bg-slate-900 hover:bg-blue-700 disabled:opacity-50 text-white rounded-sm px-4 py-2">
            {busy ? "Importing…" : "Import"}
          </button>
        </div>
      </div>
    </div>
  );
}
